var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import _get from 'lodash/get';
import _set from 'lodash/set';
import _unset from 'lodash/unset';
import _has from 'lodash/has';
import _merge from 'lodash/merge';
import _mergeWith from 'lodash/mergeWith';
import _isEqual from 'lodash/isEqual';
import { observable, action, computed, set, toJS, makeObservable } from 'mobx';
import Connector from './connector';
import ModelError from './modelError';
import Utils from './utils';
const connector = new Connector();
export { ModelError };
/* eslint-disable-next-line */
function defaultValidator(attributes) {
    return true;
}
function setOverwritePolicy(objValue, srcValue) {
    if (Array.isArray(srcValue))
        return srcValue;
    if (srcValue === Object(srcValue))
        return srcValue;
}
function setMergePolicy(objValue, srcValue) {
    if (Array.isArray(objValue))
        return objValue.concat(srcValue);
}
export default class Model {
    isLoaded = false;
    isDestroyed = false;
    isDirty = false;
    collection = undefined;
    options = {};
    idAttribute = 'id';
    _attributes = {};
    pendingRequestCount = 0;
    // TO OVERRIDE IF REQUIRED
    static get connector() {
        return connector;
    }
    static get path() {
        return '';
    }
    static get defaults() {
        return {};
    }
    static get validator() {
        return defaultValidator;
    }
    constructor(attributes = {}, options = {}) {
        this.options = options;
        if (options.collection)
            this.collection = options.collection;
        if (options.loaded)
            this.isLoaded = true;
        if (options.transform)
            attributes = this.transform(attributes);
        // Setup attributes with defaults
        this.reset(attributes, { dirty: !!options.dirty });
        this.initialize(options);
        makeObservable(this);
    }
    /* istanbul ignore next */
    toString() {
        return `[${this.constructor.name} id="${this.id}\n${JSON.stringify(this._attributes, null, 2)}\n]`;
    }
    /* istanbul ignore next */
    toJSON() {
        return this.toString();
    }
    get id() {
        return this.get(this.idAttribute);
    }
    get attributes() {
        return toJS(this._attributes);
    }
    get isNew() {
        return !this.id;
    }
    get isLoading() {
        return !!(this.pendingRequestCount > 0 || (this.collection && this.collection.isLoading));
    }
    // Attribute methods
    validateAttributes(attributes) {
        try {
            this.modelClass.validator(attributes);
        }
        catch (err) {
            throw new ModelError('Invalid model attribute', err);
        }
        return this;
    }
    get(attribute, defaultValue = undefined) {
        return _get(this._attributes, attribute, defaultValue);
    }
    has(attribute) {
        return _has(this._attributes, attribute);
    }
    is(attribute, value) {
        return _isEqual(this.get(attribute), value);
    }
    setId(id) {
        this.set(this.idAttribute, id);
        return this;
    }
    set(attribute, value = undefined, options = {}) {
        const newAttributes = {};
        let newValue = {};
        // Apply current attributes and preserve arrays
        _merge(newAttributes, this.attributes);
        if (undefined === value) {
            // Overwrite all
            newValue = attribute;
        }
        else {
            // Update a property
            _set(newValue, attribute, value);
        }
        // Merge function
        const mergeFn = options.merge ? setMergePolicy : setOverwritePolicy;
        _mergeWith(newAttributes, newValue, mergeFn);
        this.validateAttributes(newAttributes);
        set(this._attributes, newAttributes);
        this.isDirty = true;
        if (this.collection)
            this.collection.sort();
        return this;
    }
    merge(attribute, value = undefined) {
        return this.set(attribute, value, { merge: true });
    }
    unset(attribute) {
        if (this.has(attribute)) {
            const newAttributes = _merge({}, this.attributes);
            _unset(newAttributes, attribute);
            this.validateAttributes(newAttributes);
            this._attributes = newAttributes;
            this.isDirty = true;
            if (this.collection)
                this.collection.sort();
        }
        return this;
    }
    reset(attributes, options = { dirty: true }) {
        const newAttributes = {};
        _merge(newAttributes, this.defaults, attributes);
        this.validateAttributes(newAttributes);
        this._attributes = newAttributes;
        this.isDirty = !!options.dirty;
        if (this.collection)
            this.collection.sort();
        return this;
    }
    clear() {
        this.isLoaded = false;
        return this.reset({});
    }
    clean() {
        // Consider the attributes up to date
        this.isDirty = false;
    }
    // API methods
    fetch(options = {}) {
        return new Promise((resolve, reject) => {
            const fetchOptions = this.prepareFetchOptions(options);
            this.pendingRequestCount++;
            this.connector.fetch(this, fetchOptions)
                .then((result) => {
                this.onFetchSuccess(result, fetchOptions);
                resolve(this);
            }, (err) => {
                this.onFetchError(err);
            })
                .catch((err) => {
                reject(err);
            });
        });
    }
    prepareFetchOptions(options) {
        return options;
    }
    onFetchSuccess(result, options) {
        this.reset(this.transform(result.attributes), { dirty: false });
        this.isLoaded = true;
        this.isDestroyed = false;
        this.isDirty = false;
        this.onSuccess(result);
    }
    onFetchError(err) {
        this.onError('Error fetching model', err);
    }
    save(options = {}) {
        return new Promise((resolve, reject) => {
            const saveOptions = this.prepareSaveOptions(options);
            this.pendingRequestCount++;
            this.connector.save(this, saveOptions)
                .then((result) => {
                this.onSaveSuccess(result, saveOptions);
                resolve(this);
            }, (err) => {
                this.onSaveError(err);
            })
                .catch((err) => {
                reject(err);
            });
        });
    }
    prepareSaveOptions(options) {
        return options;
    }
    onSaveSuccess(result, options) {
        this.reset(this.transform(result.attributes), { dirty: false });
        this.isLoaded = true;
        this.isDestroyed = false;
        this.isDirty = false;
        this.onSuccess(result);
    }
    onSaveError(err) {
        this.onError('Error creating model', err);
    }
    destroy(options = {}) {
        return new Promise((resolve, reject) => {
            const destroyOptions = this.prepareDestroyOptions(options);
            this.pendingRequestCount++;
            this.connector.destroy(this, destroyOptions)
                .then((result) => {
                this.onDestroySuccess(result, destroyOptions);
                if (this.collection)
                    this.collection.remove(this);
                resolve(this);
            }, (err) => {
                this.onDestroyError(err);
            })
                .catch((err) => {
                reject(err);
            });
        });
    }
    prepareDestroyOptions(options) {
        return options;
    }
    onDestroySuccess(result, options) {
        this.onSuccess(result);
        this.isLoaded = false;
        this.isDestroyed = true;
        this.isDirty = false;
    }
    onDestroyError(err) {
        this.onError('Error deleting model', err);
    }
    // Global handlers
    onSuccess(result) {
        this.pendingRequestCount--;
    }
    onError(message, err) {
        this.pendingRequestCount--;
        throw new ModelError(message + ' -> ' + err.message, err);
    }
    // Utils
    clone(options = {}) {
        return new this.constructor(this.attributes, options);
    }
    // Accessors for static properties from instance
    get modelClass() {
        return this.constructor;
    }
    get modelName() {
        return this.modelClass.name;
    }
    get connector() {
        return this.modelClass.connector;
    }
    get path() {
        return Utils.format(this.modelClass.path, this.attributes);
    }
    get defaults() {
        return this.modelClass.defaults;
    }
    initialize(options) {
        // To override
    }
    // Transform attributes before setting them on the model (mapping ?)
    get untransformedAttributes() {
        return this.untransform();
    }
    transform(attributes) {
        return attributes;
    }
    untransform() {
        return this.attributes;
    }
}
__decorate([
    observable
], Model.prototype, "isLoaded", void 0);
__decorate([
    observable
], Model.prototype, "isDestroyed", void 0);
__decorate([
    observable
], Model.prototype, "isDirty", void 0);
__decorate([
    observable
], Model.prototype, "_attributes", void 0);
__decorate([
    observable
], Model.prototype, "pendingRequestCount", void 0);
__decorate([
    computed
], Model.prototype, "id", null);
__decorate([
    computed
], Model.prototype, "isNew", null);
__decorate([
    computed
], Model.prototype, "isLoading", null);
__decorate([
    action
], Model.prototype, "set", null);
__decorate([
    action
], Model.prototype, "merge", null);
__decorate([
    action
], Model.prototype, "unset", null);
__decorate([
    action
], Model.prototype, "reset", null);
__decorate([
    action
], Model.prototype, "clear", null);
__decorate([
    action
], Model.prototype, "clean", null);
__decorate([
    action
], Model.prototype, "fetch", null);
__decorate([
    action
], Model.prototype, "onFetchSuccess", null);
__decorate([
    action
], Model.prototype, "onFetchError", null);
__decorate([
    action
], Model.prototype, "save", null);
__decorate([
    action
], Model.prototype, "onSaveSuccess", null);
__decorate([
    action
], Model.prototype, "onSaveError", null);
__decorate([
    action
], Model.prototype, "destroy", null);
__decorate([
    action
], Model.prototype, "onDestroySuccess", null);
__decorate([
    action
], Model.prototype, "onDestroyError", null);
__decorate([
    action
], Model.prototype, "onSuccess", null);
__decorate([
    action
], Model.prototype, "onError", null);
//# sourceMappingURL=model.js.map